import React, { FC } from 'react';
import { Form, Modal } from 'react-bootstrap';
import styles from './GenerateTranslationModal.module.css';
import { CustomDropdown } from '../../molecules';
import { Button } from '../../atoms';
import cn from 'classnames';
import { DropdownItem } from '../../molecules/CustomDropdown/CustomDropdown';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import { showError, showSuccess } from '../../../utils/Notifications';
import { PromiseSettledResult } from '../../../utils/types/types';

interface GenerateTranslationModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  fromLanguage: LocalesLanguagesConstants;
  setFromLanguage: React.Dispatch<
    React.SetStateAction<LocalesLanguagesConstants>
  >;
  toLanguage: LocalesLanguagesConstants;
  setToLanguage: React.Dispatch<
    React.SetStateAction<LocalesLanguagesConstants>
  >;
  generateTranslation: () => Promise<void>;
  generateAllTranslations: () => Promise<PromiseSettledResult<void>[]>;
}

const GenerateTranslationModal: FC<GenerateTranslationModalProps> = ({
  show,
  setShow,
  fromLanguage,
  setFromLanguage,
  toLanguage,
  setToLanguage,
  generateTranslation,
  generateAllTranslations,
}) => {
  const languages: DropdownItem[] = Object.keys(LocalesLanguagesConstants).map(
    (key) => {
      return {
        label:
          LocalesLanguagesConstants[
            key as keyof typeof LocalesLanguagesConstants
          ],
        eventKey: key,
      };
    }
  );

  return (
    <Modal centered show={show} onHide={() => setShow(false)}>
      <div className={cn(styles.modal)}>
        <Modal.Body className={styles.modalBody}>
          <Form.Group className="mb-3">
            <Form.Label>Source language</Form.Label>
            <CustomDropdown
              initialTitle={
                languages.find((i) => i.label === fromLanguage)!.label
              }
              items={languages}
              onSelectOption={(item) => {
                setFromLanguage(
                  LocalesLanguagesConstants[
                    item.eventKey as keyof typeof LocalesLanguagesConstants
                  ]
                );
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Target language</Form.Label>
            <CustomDropdown
              initialTitle={
                languages.find((i) => i.label === toLanguage)!.label
              }
              items={languages}
              onSelectOption={(item) => {
                setToLanguage(
                  LocalesLanguagesConstants[
                    item.eventKey as keyof typeof LocalesLanguagesConstants
                  ]
                );
              }}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className={styles.modalButtons}>
          <Button
            className={cn(styles.button)}
            variant="dark"
            onClick={() => {
              generateTranslation()
                .then(() =>
                  showSuccess(
                    'Translation success. Select the appropriate language to view the changes'
                  )
                )
                .catch(() => showError('Translation error'));
            }}
          >
            Translate
          </Button>
          <Button
            className={cn(styles.button)}
            variant="dark"
            onClick={() => {
              generateAllTranslations()
                .then((results) => {
                  const flag = results
                    .map((result) =>
                      result.status === 'fulfilled' ? true : false
                    )
                    .reduce((accumulator, currentValue) => {
                      if (!accumulator) {
                        return false;
                      }

                      if (currentValue) {
                        return true;
                      }

                      return false;
                    }, true);

                  if (flag) {
                    showSuccess(
                      'Translation success. Select the appropriate language to view the changes'
                    );
                  } else {
                    showError('Translation error');
                  }
                })
                .catch((e) => e);
            }}
          >
            Translate All
          </Button>
          <Button
            className={cn(styles.cancelButton)}
            variant="light"
            onClick={() => setShow(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default GenerateTranslationModal;
